import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Player, GolfRound, Signup, AppView, AppData, Course } from './types';
import { PlayerView } from './components/PlayerView';
import { AdminView } from './components/AdminView';
import { 
  subscribeToAppData, 
  saveAppData, 
  logout
} from './services/firebase';

// Local Storage Key
const USER_STORAGE_KEY = 'teetime_user';

const App: React.FC = () => {
  const [view, setView] = useState<AppView>('player');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<{ phoneNumber: string, uid: string, isAdmin: boolean } | null>(null);
  const [data, setData] = useState<AppData>({
    players: [],
    courses: [],
    rounds: [],
    signups: []
  });

  // Auth State
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [authStep, setAuthStep] = useState<'phone' | 'pin'>('phone');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  
  // Lockout State
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);

  const isInitialized = useRef(false);
  const skipNextSave = useRef(false);

  // Helper to normalize phone numbers for comparison
  const normalizePhone = (num: string) => {
    const digits = num.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length > 10) return `+${digits}`;
    return digits;
  };

  // 1. Load User from Local Storage
  useEffect(() => {
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse stored user", e);
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
  }, []);

  // 2. Data Listener
  useEffect(() => {
    const unsubscribe = subscribeToAppData((cloudData) => {
      skipNextSave.current = true;
      setData(cloudData);
      isInitialized.current = true;
      setLoading(false);
    });
    
    const timer = setTimeout(() => setLoading(false), 4000);
    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  // 3. Save to Cloud
  useEffect(() => {
    if (isInitialized.current) {
      if (skipNextSave.current) {
        skipNextSave.current = false;
        return;
      }
      saveAppData(data);
    }
  }, [data]);

  // 4. Auto-associate player on login / update local user data
  useEffect(() => {
    if (user && data.players.length > 0) {
      const userPhone = normalizePhone(user.phoneNumber || ''); 
      const existingProfile = data.players.find(p => normalizePhone(p.phoneNumber || '') === userPhone);
      
      if (existingProfile) {
        // Update local user state if admin status changes
        if (existingProfile.isAdmin !== user.isAdmin) {
            const updatedUser = { ...user, isAdmin: !!existingProfile.isAdmin };
            setUser(updatedUser);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
        }

        // Ensure UID is linked
        if (existingProfile.uid !== user.uid) {
            setData(prev => ({
            ...prev,
            players: prev.players.map(p => 
                normalizePhone(p.phoneNumber || '') === userPhone ? { ...p, uid: user.uid } : p
            )
            }));
        }
      }
    }
  }, [user, data.players]);

  const adminCount = useMemo(() => data.players.filter(p => p.isAdmin).length, [data.players]);
  const activePlayer = useMemo(() => data.players.find(p => p.uid === user?.uid), [data.players, user]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    // Check Lockout
    if (lockoutUntil && Date.now() < lockoutUntil) {
        const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
        setAuthError(`Too many failed attempts. Try again in ${remaining}s.`);
        return;
    } else if (lockoutUntil) {
        setLockoutUntil(null);
        setFailedAttempts(0);
    }

    setIsProcessingAuth(true);

    const formattedPhone = normalizePhone(phone);
    if (formattedPhone.length < 11) {
      setAuthError("Please enter a valid 10-digit phone number.");
      setIsProcessingAuth(false);
      return;
    }

    // Check Roster
    const player = data.players.find(p => normalizePhone(p.phoneNumber || '') === formattedPhone);

    if (!player && data.players.length > 0) {
        handleFailedAttempt();
        setAuthError("Access Denied: This number is not on the player roster.");
        setIsProcessingAuth(false);
        return;
    }

    // If Admin, require PIN
    if (player?.isAdmin) {
        setAuthStep('pin');
        setIsProcessingAuth(false);
        return;
    }

    // Success (Non-Admin or First User)
    const newUser = {
        phoneNumber: formattedPhone,
        uid: player?.uid || `user-${Date.now()}`,
        isAdmin: false
    };
    
    completeLogin(newUser);
  };

  const handleVerifyPin = (e: React.FormEvent) => {
      e.preventDefault();
      setAuthError(null);

       // Check Lockout
      if (lockoutUntil && Date.now() < lockoutUntil) {
        const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
        setAuthError(`Too many failed attempts. Try again in ${remaining}s.`);
        return;
      }

      const formattedPhone = normalizePhone(phone);
      const player = data.players.find(p => normalizePhone(p.phoneNumber || '') === formattedPhone);

      // Allow login if PIN matches OR if no PIN is set and user enters "0000" (Migration fallback)
      if (player && (player.pin === pin || (!player.pin && pin === '0000'))) {
          // Success
          const newUser = {
            phoneNumber: formattedPhone,
            uid: player.uid || `user-${Date.now()}`,
            isAdmin: true
        };
        completeLogin(newUser);
      } else {
          handleFailedAttempt();
          setAuthError("Incorrect PIN.");
      }
  };

  const handleFailedAttempt = () => {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= 3) {
          setLockoutUntil(Date.now() + 30000); // 30 seconds
          setAuthError("Too many failed attempts. Locked for 30s.");
      }
  };

  const completeLogin = (newUser: { phoneNumber: string, uid: string, isAdmin: boolean }) => {
      setUser(newUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(newUser));
      setIsProcessingAuth(false);
      setAuthStep('phone');
      setPhone('');
      setPin('');
      setFailedAttempts(0);
      setLockoutUntil(null);
  };

  const handleLogout = () => {
    logout(); // Clear any firebase auth if needed (no-op currently)
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
    setView('player');
    setPhone('');
    setPin('');
    setAuthStep('phone');
  };

  const handleInitializeAdmin = useCallback((name: string) => {
    if (!user || !name.trim()) return;
    const newPlayer: Player = {
      id: Math.random().toString(36).substr(2, 9),
      name: name.trim(),
      phoneNumber: user.phoneNumber,
      uid: user.uid,
      isAdmin: true,
      handicapIndex: null,
      pin: '0000' // Default PIN
    };
    setData(prev => ({
      ...prev,
      players: [...prev.players, newPlayer]
    }));
    
    // Update local user to admin
    const updatedUser = { ...user, isAdmin: true };
    setUser(updatedUser);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
    
    setView('admin');
    alert("Admin initialized! Default PIN is 0000. Please change it in the roster.");
  }, [user]);

  const handleClaimAdminRole = useCallback(() => {
    if (!activePlayer) return;
    const defaultPin = '0000';
    setData(prev => ({
      ...prev,
      players: prev.players.map(p => p.id === activePlayer.id ? { ...p, isAdmin: true, pin: defaultPin } : p)
    }));
    
    // Update local user
    const updatedUser = { ...user!, isAdmin: true };
    setUser(updatedUser);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));

    setView('admin');
    alert(`Admin role claimed! Your default PIN is ${defaultPin}.`);
  }, [activePlayer, user]);

  const handleLinkPlayer = useCallback((playerId: string) => {
    if (!user) return;
    setData(prev => ({
      ...prev,
      players: prev.players.map(p => p.id === playerId ? { ...p, uid: user.uid } : p)
    }));
  }, [user]);

  const handleUpdatePlayers = useCallback((players: Player[]) => {
    setData(prev => ({ ...prev, players }));
  }, []);

  const handleUpdatePlayer = useCallback((updatedPlayer: Player) => {
    setData(prev => ({
      ...prev,
      players: prev.players.map(p => p.id === updatedPlayer.id ? updatedPlayer : p)
    }));
  }, []);

  const handleUpdateRounds = useCallback((rounds: GolfRound[]) => {
    setData(prev => ({ ...prev, rounds }));
  }, []);

  const handleUpdateCourses = useCallback((courses: Course[]) => {
    setData(prev => ({ ...prev, courses }));
  }, []);

  const handleUpdateSignups = useCallback((signups: Signup[]) => {
    setData(prev => ({ ...prev, signups }));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-emerald-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-emerald-100 font-bold tracking-widest uppercase text-[10px]">Loading Green...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-emerald-900 flex items-center justify-center p-6">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl max-w-sm w-full text-center relative overflow-hidden">
          {/* Login Logo - Expects 'logo-login.png' in public folder */}
          <img 
            src="/logo-login.png" 
            alt="Tee Time Connect" 
            className="h-24 w-auto mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
              e.currentTarget.nextElementSibling?.nextElementSibling?.classList.remove('hidden');
            }}
          />
          {/* Fallback (Hidden by default) */}
          <div className="text-6xl mb-6 hidden">⛳</div>
          <h2 className="text-2xl font-black text-slate-800 mb-2 tracking-tight hidden">TeeTime</h2>
          
          <p className="text-slate-400 mb-10 text-xs font-medium px-4 leading-relaxed">
            {authStep === 'phone' 
              ? "Access is restricted to players on the official roster." 
              : `Enter your 4-digit Admin PIN.`}
          </p>
          <form onSubmit={authStep === 'phone' ? handleLogin : handleVerifyPin} className="space-y-6">
            {authStep === 'phone' ? (
              <div className="space-y-2 text-left">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mobile Number</label>
                <input type="tel" placeholder="(555) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full p-5 border border-slate-100 rounded-2xl text-center font-black text-2xl tracking-wider focus:ring-4 focus:ring-emerald-500/10 outline-none bg-slate-50 transition-all shadow-inner" required autoFocus />
              </div>
            ) : (
              <div className="space-y-2 text-left">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 text-center block">Admin PIN</label>
                <input type="password" maxLength={4} inputMode="numeric" placeholder="••••" value={pin} onChange={(e) => setPin(e.target.value)} className="w-full p-5 border border-slate-100 rounded-2xl text-center font-black text-4xl tracking-[0.4em] focus:ring-4 focus:ring-emerald-500/10 outline-none bg-slate-50 transition-all shadow-inner" required autoFocus />
              </div>
            )}
            {authError && (
              <div className="space-y-4">
                <div className="bg-rose-50 border border-rose-100 p-5 rounded-2xl">
                  <p className="text-[11px] text-rose-600 font-black uppercase tracking-tight mb-1">Error</p>
                  <p className="text-xs text-rose-500 font-bold leading-normal">{authError}</p>
                </div>
              </div>
            )}
            <button disabled={isProcessingAuth || (lockoutUntil !== null && Date.now() < lockoutUntil)} type="submit" className="w-full bg-emerald-700 text-white py-5 rounded-2xl font-black hover:bg-emerald-800 transition-all shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm uppercase tracking-[0.2em]">
              {isProcessingAuth ? 'Processing...' : (authStep === 'phone' ? 'Verify Number' : 'Unlock Admin')}
            </button>
            {authStep === 'pin' && (
              <button type="button" onClick={() => { setAuthStep('phone'); setPin(''); setAuthError(null); }} className="text-[10px] text-slate-400 hover:text-emerald-700 font-black uppercase tracking-widest mt-4 block mx-auto">← Back</button>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20">
      <header className="bg-emerald-700 text-white shadow-xl sticky top-0 z-30 print:hidden pt-safe">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* Logo Image - Expects 'logo.png' in the public folder */}
            <img 
              src="/logo.png" 
              alt="Tee Time Connect" 
              className="h-10 w-auto object-contain rounded-md" 
              onError={(e) => {
                // Fallback if image is missing
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            {/* Fallback Text Logo (Hidden by default unless image fails) */}
            <h1 className="text-xl font-black tracking-tighter hidden">⛳ TEETIME</h1>
          </div>
          
          {(user.isAdmin || (activePlayer && adminCount === 0)) && (
            <div className="flex bg-emerald-800/60 backdrop-blur-sm rounded-xl p-1 shadow-inner">
              {user.isAdmin && (
                <>
                  <button onClick={() => setView('player')} className={`px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${view === 'player' ? 'bg-white text-emerald-800 shadow-md' : 'text-emerald-100 hover:text-white'}`}>Play</button>
                  <button onClick={() => setView('admin')} className={`px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${view === 'admin' ? 'bg-white text-emerald-800 shadow-md' : 'text-emerald-100 hover:text-white'}`}>Admin</button>
                </>
              )}
              {activePlayer && adminCount === 0 && (
                <button onClick={handleClaimAdminRole} className="px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest bg-rose-500 text-white animate-pulse">Setup Admin</button>
              )}
            </div>
          )}

          <button onClick={handleLogout} className="px-4 py-2 bg-emerald-800 hover:bg-rose-700 rounded-xl transition-all border border-emerald-600 shadow-lg active:scale-95 text-[10px] font-black uppercase tracking-widest">
            Log Out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 print:w-full print:max-w-none print:p-0">
        {!activePlayer ? (
          <div className="bg-white p-12 rounded-[2.5rem] shadow-xl border border-slate-100 text-center animate-in fade-in zoom-in duration-500">
            <div className="w-24 h-24 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center text-5xl mx-auto mb-8 shadow-inner">🏌️</div>
            <h2 className="text-3xl font-black text-slate-800 mb-3 tracking-tight">Identity Pending</h2>
            <p className="text-slate-400 mb-10 text-sm max-w-sm mx-auto leading-relaxed font-medium">
              We've verified your phone, but you aren't linked to a player profile yet. 
            </p>
            {adminCount === 0 ? (
              <div className="space-y-6 max-w-xs mx-auto">
                <p className="bg-amber-50 text-amber-700 p-4 rounded-xl text-xs font-bold border border-amber-100">No administrators detected. You can initialize this system as the primary administrator.</p>
                <input id="initial-admin-name" type="text" placeholder="Enter Your Name" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 font-black text-slate-700 text-center" />
                <button onClick={() => {
                  const input = document.getElementById('initial-admin-name') as HTMLInputElement;
                  handleInitializeAdmin(input.value);
                }} className="w-full bg-emerald-700 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-[0.2em] shadow-2xl hover:bg-emerald-800 transition-all">🚀 Initialize Admin</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.players.filter(p => !p.uid).map(p => (
                  <button key={p.id} onClick={() => handleLinkPlayer(p.id)} className="p-6 border-2 border-slate-50 rounded-2xl bg-slate-50/50 hover:border-emerald-500 hover:bg-white hover:shadow-2xl transition-all text-left group border-dashed">
                    <div className="font-black text-slate-700 text-xl group-hover:text-emerald-700">{p.name}</div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 group-hover:text-emerald-500">Claim Profile</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          view === 'player' ? (
            <PlayerView players={data.players} rounds={data.rounds} courses={data.courses} signups={data.signups} onUpdateSignups={handleUpdateSignups} onUpdatePlayer={handleUpdatePlayer} activePlayer={activePlayer!} />
          ) : (
            <AdminView players={data.players} rounds={data.rounds} courses={data.courses} signups={data.signups} onUpdatePlayers={handleUpdatePlayers} onUpdateRounds={handleUpdateRounds} onUpdateCourses={handleUpdateCourses} onUpdateSignups={handleUpdateSignups} authenticatedUserPhone={user.phoneNumber || undefined} />
          )
        )}
      </main>
      <footer className="fixed bottom-0 w-full bg-white/90 backdrop-blur-md border-t border-slate-100 py-3 pb-safe text-center text-[9px] font-black text-slate-300 uppercase tracking-[0.4em] z-20 print:hidden">TeeTime Connect • SECURE ACCESS</footer>
    </div>
  );
};

export default App;